import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { errorMessage } from '@/shared/api/httpClient'
import { roomsApi } from '@/shared/api/roomsApi'
import { useAsync } from '@/shared/api/useAsync'
import { Blueprint, Corners } from '@/shared/ui/Blueprint'
import { devicesApi } from '@/features/devices/devicesApi'
import type { Finding } from '@/features/devices/types'

const REVEAL_MS = 450
const STEPS = [
  { n: 1, label: 'Busca' },
  { n: 2, label: 'Confirmação' },
]

export function AddDevicePage() {
  const navigate = useNavigate()
  const [driversVersion, setDriversVersion] = useState(0)
  const { data: base } = useAsync(async () => {
    const [rooms, drivers] = await Promise.all([roomsApi.list(), devicesApi.drivers()])
    return { rooms, drivers }
  }, [driversVersion])
  const rooms = base?.rooms ?? []
  const drivers = base?.drivers ?? []

  const [step, setStep] = useState<1 | 2>(1)
  const [searching, setSearching] = useState(true)
  const [findings, setFindings] = useState<Finding[]>([])
  const [notices, setNotices] = useState<string[]>([])
  const [visible, setVisible] = useState(0)
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [roomChoice, setRoomChoice] = useState<Record<string, string>>({})
  const [cred, setCred] = useState<Record<string, Record<string, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [cloudCred, setCloudCred] = useState<Record<string, Record<string, string>>>({})
  const [savingCloud, setSavingCloud] = useState<string | null>(null)

  const timers = useRef<number[]>([])
  const generation = useRef(0)

  const search = useCallback(async () => {
    const mine = (generation.current = generation.current + 1)
    timers.current.forEach(clearTimeout)
    timers.current = []
    setSearching(true)
    setFindings([])
    setNotices([])
    setVisible(0)
    setSelected({})
    setRoomChoice({})
    setError(null)
    try {
      const { findings: found, notices: found_notices } = await devicesApi.discover()
      if (mine !== generation.current) return
      setFindings(found)
      setNotices(found_notices)
      // ponytail: a API responde de uma vez; revelar aos poucos é só a cadência da busca no design.
      found.forEach((_, i) => timers.current.push(window.setTimeout(() => setVisible(i + 1), REVEAL_MS * (i + 1))))
      timers.current.push(window.setTimeout(() => setSearching(false), REVEAL_MS * (found.length + 1)))
    } catch (err) {
      if (mine !== generation.current) return
      setError(errorMessage(err))
      setSearching(false)
    }
  }, [])

  useEffect(() => {
    search()
    return () => {
      generation.current = generation.current + 1
      timers.current.forEach(clearTimeout)
    }
  }, [search])

  const shown = findings.slice(0, visible)
  const chosen = shown.filter((f) => selected[f.external_id])
  const allChosen = shown.length > 0 && chosen.length === shown.length

  // Contas de fabricante que exigem conta e ainda não têm credencial guardada (uma por família).
  const pending = [...new Set(chosen.filter((f) => f.requires_account && !f.has_credentials).map((f) => f.credential_key))].map((id) => {
    const driver = drivers.find((d) => d.credential_key === id && d.credential_fields.length > 0)
    return {
      id,
      manufacturer: driver?.manufacturer ?? chosen.find((f) => f.credential_key === id)?.manufacturer ?? id,
      fields: driver?.credential_fields ?? [],
    }
  })

  // Contas de nuvem: sem elas o fabricante nem entra na busca (ex.: Tuya).
  const cloudAccounts = drivers
    .filter((d) => d.cloud && !d.has_credentials && d.credential_fields.length > 0)
    .filter((d, i, all) => all.findIndex((x) => x.credential_key === d.credential_key) === i)
  const cloudOk = (d: (typeof drivers)[number]) => d.credential_fields.every((f) => (cloudCred[d.credential_key]?.[f.name] ?? '').trim())

  async function saveCloud(d: (typeof drivers)[number]) {
    setSavingCloud(d.credential_key)
    setError(null)
    try {
      await devicesApi.saveCredentials(d.credential_key, cloudCred[d.credential_key] ?? {})
      setDriversVersion((v) => v + 1)
      await search()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSavingCloud(null)
    }
  }
  const credOk = pending.every((p) => p.fields.every((f) => (cred[p.id]?.[f.name] ?? '').trim()))

  const roomOptions = (f: Finding) => {
    const names = rooms.map((r) => r.name)
    const suggested = f.suggested_room
    return suggested && !names.some((n) => n.toLowerCase() === suggested.toLowerCase()) ? [...names, suggested] : names
  }
  const roomFor = (f: Finding) => roomChoice[f.external_id] ?? f.suggested_room

  const nextDisabled = submitting || searching || chosen.length === 0 || (step === 2 && !credOk)
  const hint =
    step === 1
      ? searching
        ? 'Aguarde a varredura terminar ou siga com o que já apareceu.'
        : `${chosen.length} de ${shown.length} selecionados`
      : pending.length && !credOk
        ? 'Faltam as credenciais dos fabricantes que exigem conta na nuvem.'
        : `${chosen.length} ${chosen.length === 1 ? 'dispositivo entra' : 'dispositivos entram'} em listar_dispositivos`

  async function conclude() {
    setSubmitting(true)
    setError(null)
    try {
      const { devices } = await devicesApi.adopt({
        devices: chosen.map((f) => ({
          external_id: f.external_id,
          driver: f.driver,
          name: f.name,
          address: f.address,
          requires_account: f.requires_account && !f.has_credentials,
          room_name: roomFor(f) || null,
        })),
        credentials: Object.fromEntries(pending.map((p) => [p.id, cred[p.id] ?? {}])),
      })
      const n = devices.length
      navigate('/dispositivos', {
        state: { notice: n === 1 ? '1 dispositivo pareado e já visível para os agentes autorizados.' : `${n} dispositivos pareados e já visíveis para os agentes autorizados.` },
      })
    } catch (err) {
      setError(errorMessage(err))
      setSubmitting(false)
    }
  }

  return (
    <div style={{ maxWidth: 980 }}>
      <button type="button" className="btn btn-ghost" onClick={() => navigate('/dispositivos')} style={{ marginBottom: 16, minHeight: 42 }}>
        ← Cancelar
      </button>
      <h2 style={{ margin: '0 0 4px' }}>Adicionar dispositivo</h2>
      <p className="text-muted" style={{ fontSize: 14, margin: '0 0 16px', maxWidth: '70ch' }}>
        A busca acontece sozinha: rede local para Tapo/Kasa, Sonoff, Yeelight, Hue e WiZ; nuvem Tuya para Positivo, Intelbras, Elgin, Multilaser e outras marcas nacionais. Você só confirma onde cada um fica.
      </p>
      <Blueprint style={{ padding: '12px 14px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderColor: 'var(--color-accent-300)', background: 'var(--color-accent-100)' }}>
        <span className="tag tag-accent">pelo assistente também</span>
        <span style={{ fontSize: 14, color: 'var(--color-accent-800)' }}>
          É só pedir: “Claude, adiciona a luz do corredor no quarto” — ele roda esta mesma busca via MCP e cadastra por você.
        </span>
      </Blueprint>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 26 }}>
        {STEPS.map((st) => (
          <div key={st.n} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className={`tag ${step === st.n ? 'tag-accent' : step > st.n ? 'tag-outline' : 'tag-neutral'}`} style={{ minWidth: 26, justifyContent: 'center' }}>
              {st.n}
            </span>
            <span className="heading" style={{ fontSize: 15 }}>{st.label}</span>
            {st.n === 1 && <span className="text-muted" style={{ margin: '0 6px' }}>—</span>}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingBottom: 10, borderBottom: '1px solid var(--color-divider)', marginBottom: 16 }}>
            {searching && <span className="pulse" />}
            <h6 style={{ margin: 0, color: 'var(--color-accent-700)' }}>{searching ? 'Procurando dispositivos…' : 'Busca concluída'}</h6>
            <span className="text-muted" style={{ fontSize: 13 }}>
              {searching
                ? `${shown.length} encontrados até agora · rede local`
                : `${shown.length} ${shown.length === 1 ? 'dispositivo encontrado' : 'dispositivos encontrados'} · ${chosen.length} selecionados`}
            </span>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn-ghost" onClick={() => setSelected(Object.fromEntries(shown.map((f) => [f.external_id, !allChosen])))}>
              {allChosen ? 'Limpar seleção' : 'Selecionar todos'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={search} disabled={searching}>
              Buscar de novo
            </button>
          </div>

          {cloudAccounts.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h6 style={{ margin: '0 0 10px', color: 'var(--color-accent-700)' }}>Contas de nuvem</h6>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))', gap: 16 }}>
                {cloudAccounts.map((d) => (
                  <Blueprint key={d.credential_key} style={{ padding: 16 }}>
                    <div className="heading" style={{ fontSize: 17 }}>{d.manufacturer}</div>
                    <p className="text-muted" style={{ fontSize: 12, margin: '2px 0 12px' }}>
                      Atende {d.brands.join(', ')}. A busca lista os aparelhos da sua conta no app; as chaves ficam cifradas no servidor.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {d.credential_fields.map((f) => (
                        <div key={f.name} className="field">
                          <label htmlFor={`cloud-${d.credential_key}-${f.name}`}>{f.label}</label>
                          <input
                            id={`cloud-${d.credential_key}-${f.name}`}
                            className="input"
                            type={f.secret ? 'password' : 'text'}
                            autoComplete="off"
                            placeholder={f.placeholder}
                            value={cloudCred[d.credential_key]?.[f.name] ?? ''}
                            onChange={(e) => setCloudCred({ ...cloudCred, [d.credential_key]: { ...cloudCred[d.credential_key], [f.name]: e.target.value } })}
                          />
                        </div>
                      ))}
                      <button type="button" className="btn btn-secondary" onClick={() => saveCloud(d)} disabled={!cloudOk(d) || savingCloud === d.credential_key} style={{ alignSelf: 'flex-start' }}>
                        {savingCloud === d.credential_key ? 'Salvando…' : 'Salvar e buscar de novo'}
                      </button>
                    </div>
                  </Blueprint>
                ))}
              </div>
            </div>
          )}

          {!searching && notices.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {notices.map((n) => (
                <Blueprint key={n} style={{ padding: '10px 14px', fontSize: 14, borderColor: 'var(--color-accent-300)', background: 'var(--color-accent-100)', color: 'var(--color-accent-800)' }}>
                  {n}
                </Blueprint>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {shown.map((f) => (
              <Blueprint key={f.external_id} style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <label className="radio" style={{ gap: 10 }}>
                  <input type="checkbox" checked={!!selected[f.external_id]} onChange={(e) => setSelected({ ...selected, [f.external_id]: e.target.checked })} />
                  <span className="dot" style={{ borderRadius: 0 }} />
                  <span className="heading" style={{ fontSize: 17 }}>{f.name}</span>
                </label>
                <span className="tag tag-neutral">{f.protocol}</span>
                <span className="text-muted" style={{ fontSize: 13 }}>{f.detail}</span>
                <div style={{ flex: 1 }} />
                {f.requires_account && !f.has_credentials ? (
                  <span className="tag tag-outline">precisa da conta {f.manufacturer}</span>
                ) : (
                  <span className="tag tag-accent">pareado automaticamente</span>
                )}
              </Blueprint>
            ))}
          </div>

          {!searching && shown.length === 0 && (
            <Blueprint style={{ padding: 24, borderStyle: 'dashed' }}>
              <span className="text-muted" style={{ fontSize: 14 }}>
                Nada por aqui ainda. Confira se o dispositivo está ligado e na mesma rede Wi-Fi deste servidor.
              </span>
            </Blueprint>
          )}
        </div>
      )}

      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div>
            <h6 style={{ margin: '0 0 12px', color: 'var(--color-accent-700)' }}>Onde cada um fica</h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {chosen.map((f) => (
                <Blueprint key={f.external_id} style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 220 }}>
                    <div className="heading" style={{ fontSize: 17 }}>{f.name}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>{f.protocol} · {f.manufacturer}</div>
                  </div>
                  <span className="tag tag-neutral">{f.kind_label}</span>
                  <div style={{ flex: 1 }} />
                  <div className="field" style={{ flex: '1 1 170px', maxWidth: 220 }}>
                    <label htmlFor={`room-${f.external_id}`}>Cômodo sugerido</label>
                    <select id={`room-${f.external_id}`} className="input" value={roomFor(f)} onChange={(e) => setRoomChoice({ ...roomChoice, [f.external_id]: e.target.value })}>
                      <option value="">Sem cômodo</option>
                      {roomOptions(f).map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                </Blueprint>
              ))}
            </div>
          </div>

          {pending.length > 0 && (
            <div>
              <h6 style={{ margin: '0 0 12px', color: 'var(--color-accent-700)' }}>Contas que faltam</h6>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,280px),1fr))', gap: 16 }}>
                {pending.map((p) => (
                  <Blueprint key={p.id} style={{ padding: 16 }}>
                    <div className="heading" style={{ fontSize: 17 }}>{p.manufacturer}</div>
                    <p className="text-muted" style={{ fontSize: 12, margin: '2px 0 12px' }}>
                      Só este fabricante exige login na nuvem. Fica guardado no driver — o agente nunca vê.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {p.fields.map((f) => (
                        <div key={f.name} className="field">
                          <label htmlFor={`cred-${p.id}-${f.name}`}>{f.label}</label>
                          <input
                            id={`cred-${p.id}-${f.name}`}
                            className="input"
                            type={f.secret ? 'password' : 'text'}
                            autoComplete="off"
                            placeholder={f.placeholder}
                            value={cred[p.id]?.[f.name] ?? ''}
                            onChange={(e) => setCred({ ...cred, [p.id]: { ...cred[p.id], [f.name]: e.target.value } })}
                          />
                        </div>
                      ))}
                    </div>
                  </Blueprint>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 28, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-secondary" onClick={() => setStep(1)} disabled={step === 1 || submitting} style={{ minHeight: 44 }}>
          Voltar
        </button>
        <button type="button" className="btn btn-primary blueprint" onClick={() => (step === 2 ? conclude() : setStep(2))} disabled={nextDisabled} style={{ padding: '9px 18px', minHeight: 44 }}>
          <Corners />
          {step === 2 ? (submitting ? 'Pareando…' : 'Concluir') : 'Continuar'}
        </button>
        <span className="text-muted" style={{ fontSize: 13 }}>{hint}</span>
      </div>
      {error && <p className="form-error" style={{ marginTop: 12 }}>{error}</p>}
    </div>
  )
}
